import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  LoaderButtonComponent,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { AuthService } from '../../auth.service';
import { getAuthErrorMessage } from '../auth-error-messages';
import { MultifactorRecaptchaService } from '../multifactor-recaptcha.service';
import { MultifactorService } from '../multifactor.service';
import {
  E164_PHONE_PATTERN,
  IMultifactorEnrolmentResult,
} from '../multifactor.types';

interface IPhoneEnrolForm {
  phoneNumber: string;
}

interface IVerifyForm {
  code: string;
}

@Component({
  selector: 'pt-phone-enrol',
  templateUrl: './phone-enrol.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    LoaderButtonComponent,
  ],
})
export class PhoneEnrolComponent {
  private _multifactorService = inject(MultifactorService);
  private _recaptchaService = inject(MultifactorRecaptchaService);

  auth = inject(AuthService);
  phoneNumber = input<string | undefined>();
  enrolled = output<IMultifactorEnrolmentResult>();

  phoneForm = new TypedFormGroup<IPhoneEnrolForm>({
    phoneNumber: new TypedFormControl<string>('', [
      Validators.required,
      Validators.pattern(E164_PHONE_PATTERN),
    ]),
  });

  codeForm = new TypedFormGroup<IVerifyForm>({
    code: new TypedFormControl<string>('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(6),
    ]),
  });

  verificationId: string | undefined;
  isLoading = signal(false);
  errorMessage = '';

  constructor() {
    effect(() => {
      const phoneNumber = this.phoneNumber();

      if (phoneNumber) {
        this.phoneForm.patchValue({ phoneNumber: phoneNumber });
      }
    });
  }

  async sendVerification(): Promise<void> {
    if (!this.phoneForm.valid) {
      return;
    }
    this.isLoading.set(true);
    this.errorMessage = '';
    try {
      const verifier = this._recaptchaService.initRecaptcha(
        'recaptcha-container'
      );
      this.verificationId = await this._multifactorService.enrolPhone(
        this.phoneForm.value.phoneNumber,
        verifier
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('MFA phone send verification failed:', error);
      this.errorMessage = getAuthErrorMessage(error);
      this._recaptchaService.clearRecaptcha();
    } finally {
      this.isLoading.set(false);
    }
  }

  async confirmEnrolment(): Promise<void> {
    if (!this.codeForm.valid || !this.verificationId) {
      return;
    }
    this.isLoading.set(true);
    this.errorMessage = '';
    try {
      const result = await this._multifactorService.confirmPhoneEnrolment(
        this.verificationId,
        this.codeForm.value.code
      );
      this.enrolled.emit(result);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('MFA phone confirm enrolment failed:', error);
      this.errorMessage = getAuthErrorMessage(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  resetVerification(): void {
    this.verificationId = undefined;
    this.codeForm.reset();
    this._recaptchaService.clearRecaptcha();
  }
}
